/**
 * FRONT-1747: "Edit" on the Quick OK card — the card's draft state and the
 * pure transforms on it.
 *
 * The server decides which approvals are editable and hands the card a form
 * seeded from the proposal (`editForm` on the approval, built by
 * `src/autonomy/approval-edit.ts`); the card never re-derives that from raw
 * tool arguments. What goes back is an EDIT — names, types, facts, or a
 * document's title and body — never an argument object: the server builds
 * the revised call itself, so the target of the save can't be changed from
 * here.
 *
 * Kept as plain functions (like the Memory page's fact-row helpers it
 * reuses) because the frontend suite renders to static markup with no DOM:
 * every transform a click triggers is tested by calling the function the
 * click calls.
 */
import {
  addFactRow,
  removeFactRow,
  updateFactRow,
  validateFactRows,
  factRowHasError,
  type FactDraftRow,
} from '~/features/memory/entity-detail';

/**
 * Mirrors `ApprovalEditFormWire` in `src/autonomy/approval-edit.ts` — the two
 * apps don't share a build. A document's body is not repeated here; it is
 * read from the approval's own `details.args.content`.
 */
export type ApprovalEditForm =
  | {
      kind: 'memory';
      entities: Array<{
        index: number;
        existing: boolean;
        name: string | null;
        type: string | null;
        attributes: Array<{ category: string; key: string; value: string }>;
        /** FRONT-2321: an existing entry's current name, when the server could resolve it. */
        currentName?: string | null;
      }>;
    }
  | { kind: 'document'; title: string };

/** Mirrors `ApprovalEdit` in `src/autonomy/approval-edit.ts`. */
export type ApprovalEdit =
  | {
      kind: 'memory';
      entities: Array<{ index: number; name?: string; type?: string; attributes: Record<string, string> }>;
    }
  | { kind: 'document'; title: string; content: string };

export interface EntityDraft {
  /** Stable React key — the entry's position in the original proposal. */
  index: number;
  existing: boolean;
  /** The existing entry's current name, so the form can say which one it adds to. */
  currentName: string | null;
  /** `null` = the proposal set no name here, so the form shows no name field. */
  name: string | null;
  type: string | null;
  facts: FactDraftRow[];
}

export type ApprovalDraft =
  | {
      kind: 'memory';
      /** The proposal saved several entries at once — each gets its own block and a Remove. */
      batch: boolean;
      entities: EntityDraft[];
    }
  | { kind: 'document'; title: string; content: string };

/**
 * FRONT-2321: what an entry's block is called. An update names the entry it
 * adds to — a bare "an existing entry" left people guessing which one.
 */
export function entityHeading(e: EntityDraft): string {
  if (!e.existing) return 'New entry';
  return e.currentName ? `Adding to ${e.currentName}` : 'Adding to an existing entry';
}

/**
 * The line under an update with no name field, saying what stays untouched.
 * In a batch the block's heading already names the entry, so it isn't repeated.
 */
export function existingEntryNote(e: EntityDraft, batch: boolean): string {
  return batch ? 'Its other facts stay as they are.' : `${entityHeading(e)}. Its other facts stay as they are.`;
}

/** A new fact row lands in the same category the model uses for plain facts. */
const NEW_FACT_CATEGORY = 'general';

export function draftFromForm(form: ApprovalEditForm, details?: unknown): ApprovalDraft {
  if (form.kind === 'document') {
    const args = (details as { args?: { content?: unknown } } | null | undefined)?.args;
    return { kind: 'document', title: form.title, content: typeof args?.content === 'string' ? args.content : '' };
  }
  return {
    kind: 'memory',
    batch: form.entities.length > 1,
    entities: form.entities.map((e) => ({
      index: e.index,
      existing: e.existing,
      currentName: e.currentName ?? null,
      name: e.name,
      type: e.type,
      facts: e.attributes.map((a) => ({ uid: crypto.randomUUID(), category: a.category, key: a.key, value: a.value })),
    })),
  };
}

type MemoryDraft = Extract<ApprovalDraft, { kind: 'memory' }>;

function patchEntity(draft: MemoryDraft, index: number, patch: (e: EntityDraft) => EntityDraft): MemoryDraft {
  return { ...draft, entities: draft.entities.map((e) => (e.index === index ? patch(e) : e)) };
}

export function setEntityName(draft: MemoryDraft, index: number, name: string): MemoryDraft {
  return patchEntity(draft, index, (e) => ({ ...e, name }));
}

export function setEntityType(draft: MemoryDraft, index: number, type: string): MemoryDraft {
  return patchEntity(draft, index, (e) => ({ ...e, type }));
}

export function addEntityFact(draft: MemoryDraft, index: number): MemoryDraft {
  return patchEntity(draft, index, (e) => {
    const facts = addFactRow(e.facts);
    const added = facts[facts.length - 1];
    return { ...e, facts: updateFactRow(facts, added.uid, { category: NEW_FACT_CATEGORY }) };
  });
}

export function updateEntityFact(
  draft: MemoryDraft,
  index: number,
  uid: string,
  patch: Partial<Pick<FactDraftRow, 'key' | 'value'>>,
): MemoryDraft {
  return patchEntity(draft, index, (e) => ({ ...e, facts: updateFactRow(e.facts, uid, patch) }));
}

export function removeEntityFact(draft: MemoryDraft, index: number, uid: string): MemoryDraft {
  return patchEntity(draft, index, (e) => ({ ...e, facts: removeFactRow(e.facts, uid) }));
}

/** Drop one entry of a batch save. Removing every entry leaves nothing to save — a No. */
export function removeEntity(draft: MemoryDraft, index: number): MemoryDraft {
  return { ...draft, entities: draft.entities.filter((e) => e.index !== index) };
}

/**
 * A row the user added and never filled in. It isn't a mistake to point at —
 * it's just not a fact — so it is dropped rather than blocking Save.
 */
function isBlankFact(f: FactDraftRow): boolean {
  return !f.key.trim() && !f.value.trim();
}

/** True when the draft has nothing left to save, so the only honest action is No. */
export function draftIsEmpty(draft: ApprovalDraft): boolean {
  return draft.kind === 'memory' && draft.entities.length === 0;
}

/**
 * The one reason "Save this version" is unavailable, or `null` when it can be
 * sent. One decision, not a `disabled` expression beside a separate hint
 * (CLAUDE.md, FRONT-1670): the button is disabled exactly when there is a
 * sentence to show.
 */
export function draftBlocker(draft: ApprovalDraft): string | null {
  if (draft.kind === 'document') return draft.title.trim() ? null : 'The document needs a title.';
  if (draft.entities.length === 0) return null;
  for (const e of draft.entities) {
    if (e.name !== null && !e.name.trim()) return 'Every entry needs a name.';
    const facts = e.facts.filter((f) => !isBlankFact(f));
    const errors = validateFactRows(facts);
    for (const f of facts) {
      const err = errors.get(f.uid);
      if (err && factRowHasError(err)) {
        if (err.key && err.key.startsWith('Duplicate')) return 'Two facts on one entry have the same label.';
        return f.key.trim() ? `The fact "${f.key.trim()}" needs a value.` : 'A fact needs a label, like "Role".';
      }
    }
  }
  return null;
}

/** What "Save this version" sends. */
export function editFromDraft(draft: ApprovalDraft): ApprovalEdit {
  if (draft.kind === 'document') return { kind: 'document', title: draft.title, content: draft.content };
  return {
    kind: 'memory',
    entities: draft.entities.map((e) => ({
      index: e.index,
      ...(e.name !== null ? { name: e.name } : {}),
      ...(e.type !== null ? { type: e.type } : {}),
      attributes: Object.fromEntries(
        e.facts.filter((f) => !isBlankFact(f)).map((f) => [`${f.category.trim()}.${f.key.trim()}`, f.value]),
      ),
    })),
  };
}
